import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, MapPin, Repeat } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COARSE_HIT_BEHIND } from '@/lib/touch-target';
import { RowActions } from '@/components/editorial';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import { labelFor } from '@/i18n/labels';
import { resolveMsg, type AnyTFunction } from '@/i18n/msg';
import { formatMonthDay, formatTime, formatWeekday } from '@/i18n/intl';
import {
  CATEGORY_ICON,
  URGENCY_RING,
  UI_URGENCY_LABEL_KEY,
  WHEN_LABEL_KEY,
  autoTaskDescriptionMsg,
  autoTaskTitleMsg,
  categoryLabel as categoryLabelFor,
  isAllDayTask,
  uiStatusForTask,
  uiUrgencyForTask,
  type WhenKey,
} from '@/lib/task-display';
import { useCompanionName } from '@/hooks/useCompanionName';
import type { Task } from '@/types/api';

/**
 * One task, one line — the task-manager row (2026-09-10, DECISIONS "Tasks are
 * a list, not cards"). Borrowed from the shape Todoist, Things and Asana all
 * converge on: a completion circle whose ring carries the urgency, the title,
 * then the facts a parent scans for in small type — category, when, where —
 * and the row actions at the right edge. Cards said all of this in a 3-column
 * grid that stopped scanning at a dozen tasks.
 *
 * The date is written relative to the GROUP the row sits in (`when`): a row
 * under "Today" shows only the time, a row under "Next seven days" the
 * weekday, an overdue row the full date in the danger colour. The heading
 * already said the day; the row does not say it again.
 */
export interface TaskRowProps {
  task: Task;
  /** Which time bucket the list has put this row in — decides the date form. */
  when: WhenKey;
  /** The pencil: opens the request modal in edit mode. Owner + unfinished only. */
  onEdit?: (task: Task) => void;
  onComplete?: (taskId: string) => void;
  /** The trash reads "Cancel task" — the page asks first (ConfirmActionDialog). */
  onCancel?: (taskId: string) => void;
  isOwner?: boolean;
  /**
   * When set, the row is the representative of a recurring series; the
   * remaining `siblings` open beneath it as compact date rows.
   */
  series?: {
    label: string;
    siblings: Task[];
    onCancelAll?: () => void;
  };
  /** Deep-link cue (#1083): a brief ring after landing on /tasks/:taskId. */
  highlighted?: boolean;
}

type LooseT = (key: string, params?: Record<string, unknown>) => string;

/**
 * #2420 (D-2420 option A): a 44 px hit area on a coarse pointer, with no
 * change to what is drawn. An ::after reaches past the control and sits at
 * -z-10 inside the row's `isolate` stacking context, beneath every drawn
 * control, so where two hit areas overlap a neighbour a tap on a visible
 * control still hits that control. Desktop renders pixel-identical. The base
 * is the shared `COARSE_HIT_BEHIND` (#3167); the reach stays a literal class.
 *
 * The 16 px completion circle. An absolute ::after is placed against the
 * padding box, which the circle's 1 px border shrinks to 14 px, so the reach
 * is 15 px: 14 px past the drawn box each side makes 44. The row's coarse
 * 14 px gap keeps the content column out of that square.
 */
const CIRCLE_HIT = `${COARSE_HIT_BEHIND} [@media(pointer:coarse)]:after:-inset-[15px]`;
/**
 * An 11 px text action (~17 px tall) grows its OWN box to 44 px on a coarse
 * pointer, not an ::after. A -z-10 ::after only receives taps where no in-flow
 * block box of the isolated row overlaps it, and the content column, the
 * series list and its rows all do (Chromium hit-tests in paint order). So the
 * button itself is the target; 4 px of padding each side is pulled back by a
 * matching negative margin, so nothing moves sideways.
 */
const TEXT_HIT =
  'inline-flex items-center [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:px-1 [@media(pointer:coarse)]:-mx-1';

/** "Tue Sep 22 · 9:05 AM" — the weekday, the day and the time, each in the reader's locale. */
function dayDateTime(d: Date, t: LooseT): string {
  return t('tasks:row.dayDateTime', { weekday: formatWeekday(d), date: formatMonthDay(d), time: formatTime(d) });
}

/** "Tue Sep 22" — an all-day task's day, with no clock (#2410). */
function dayDate(d: Date, t: LooseT): string {
  return t('tasks:row.dayDate', { weekday: formatWeekday(d), date: formatMonthDay(d) });
}

/** A sibling date in a series: with its clock, or without one when all-day. */
function occurrenceText(task: Task, t: LooseT): string | null {
  if (!task.date_time) return null;
  const d = new Date(task.date_time);
  return isAllDayTask(task) ? dayDate(d, t) : dayDateTime(d, t);
}

function whenText(task: Task, when: WhenKey, tt: AnyTFunction): string | null {
  const t = tt as unknown as LooseT;
  if (when === 'done') {
    const at = task.completion?.completed_at;
    return at ? t('tasks:row.doneOn', { date: formatMonthDay(at) }) : t('tasks:row.done');
  }
  if (!task.date_time) return null;
  const d = new Date(task.date_time);
  if (Number.isNaN(d.getTime())) return null;
  // #2410 (D-2410): a task saved without a time says so, never a clock the
  // parent did not choose. Its stored instant is the local start of the day.
  if (isAllDayTask(task)) {
    switch (when) {
      case 'today':
      case 'tomorrow':
        return t('tasks:row.allDay');
      case 'week':
        return formatWeekday(d, { width: 'long' });
      case 'overdue':
      case 'later':
      case 'undated':
        return dayDate(d, t);
    }
  }
  switch (when) {
    case 'today':
    case 'tomorrow':
      return formatTime(d);
    case 'week':
      return t('tasks:row.weekdayTime', { weekday: formatWeekday(d, { width: 'long' }), time: formatTime(d) });
    case 'overdue':
    case 'later':
    case 'undated':
      return dayDateTime(d, t);
  }
}

export function TaskRow({
  task,
  when,
  onEdit,
  onComplete,
  onCancel,
  isOwner = false,
  series,
  highlighted = false,
}: TaskRowProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const companionName = useCompanionName();
  const [seriesExpanded, setSeriesExpanded] = useState(false);

  const isCompleted = task.status === 'completed';
  const urgency = uiUrgencyForTask(task);
  const Icon = CATEGORY_ICON[task.category];
  const categoryLabel = categoryLabelFor(task.category, t);
  // An auto-staged task speaks in the reader's language through its
  // template (#2109 R3-B14); anything else renders as the family wrote it.
  const titleMsg = autoTaskTitleMsg(task);
  const title = titleMsg ? resolveMsg(t, titleMsg) : task.title;
  const descriptionMsg = autoTaskDescriptionMsg(task);
  const description = descriptionMsg ? resolveMsg(t, descriptionMsg) : task.description;
  const inMotion = uiStatusForTask(task) === 'in_motion';
  const dateText = whenText(task, when, t);
  const place = task.location?.city || task.location?.address || null;
  const canEdit = !isCompleted && isOwner && !!onEdit;

  const facts: React.ReactNode[] = [];
  facts.push(
    <span key="cat" className="inline-flex items-center gap-1">
      {Icon ? <Icon className="h-3 w-3" aria-hidden /> : null}
      {categoryLabel}
    </span>,
  );
  if (dateText) {
    facts.push(
      <span key="when" className={cn(when === 'overdue' && 'text-status-danger-fg font-medium')}>
        {dateText}
      </span>,
    );
  }
  if (place) {
    facts.push(
      <span key="where" className="inline-flex items-center gap-1 min-w-0">
        <MapPin className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{place}</span>
      </span>,
    );
  }
  if (description) {
    facts.push(<span key="desc" className="truncate min-w-0">{description}</span>);
  }

  return (
    <li
      // `id` + `tabIndex={-1}` make the row a programmatic scroll/focus
      // target for the /tasks/:taskId deep-link (#1083). Not in the tab order.
      id={`task-${task._id}`}
      data-task-id={task._id}
      tabIndex={-1}
      className={cn(
        // The row is a programmatic focus target only (tabIndex -1, never a
        // tab stop), and programmatic focus draws nothing (design-grammar
        // §4.8) — so no focus ring here; the highlight ring below is the cue.
        // `isolate` keeps the controls' touch hit areas (#2420) beneath the drawn row.
        // #2499: the row wraps so the expanded series list can take a full
        // line of its own; `gap-y-0` keeps the list's own mt-2 the only space.
        'group relative isolate flex flex-wrap items-start gap-3 gap-y-0 rounded-md px-2 py-2 -mx-2 outline-none',
        // #2420: on touch the gap is 14 px, the circle's hit-area reach, so the
        // circle's 44 px square ends where the content column begins. At 12 px
        // the column painted over 2 px of it.
        '[@media(pointer:coarse)]:gap-3.5 [@media(pointer:coarse)]:gap-y-0',
        // #2420: a row with the "N more" toggle is at least as tall as the
        // toggle's 44 px touch box, so the box never reaches the row below.
        series && series.siblings.length > 0 && '[@media(pointer:coarse)]:min-h-11',
        'hover:bg-muted/40',
        isCompleted && 'opacity-70',
        highlighted && 'ring-2 ring-stevens-indigo ring-offset-2',
      )}
    >
      {/* The completion circle. Its ring is the urgency (Todoist's priority
          colours); a finished task shows a filled check and the button is inert. */}
      <button
        type="button"
        disabled={isCompleted || !onComplete}
        onClick={() => onComplete?.(task._id)}
        aria-label={isCompleted ? t('row.doneAria', { title }) : t('row.markDoneAria', { title })}
        title={isCompleted ? t('row.done') : t('row.markDoneTitle', { urgency: labelFor(UI_URGENCY_LABEL_KEY, urgency, t) })}
        className={cn(
          CIRCLE_HIT,
          'mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
          isCompleted
            ? 'border-status-ok-fg bg-status-ok text-status-ok-fg'
            : cn(URGENCY_RING[urgency], 'hover:bg-muted'),
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40',
        )}
      >
        {isCompleted ? <Check className="h-3 w-3" strokeWidth={3} aria-hidden /> : null}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {canEdit ? (
            <button
              type="button"
              onClick={() => onEdit?.(task)}
              className="text-left text-body font-medium text-foreground hover:underline underline-offset-2"
            >
              {title}
            </button>
          ) : (
            <span className={cn('text-body font-medium', isCompleted ? 'text-stevens-ink-3 line-through' : 'text-foreground')}>
              {title}
            </span>
          )}
          {series ? (
            <span
              className="inline-flex items-center gap-1 text-label text-stevens-indigo"
              title={t('row.recurringTitle', { count: series.siblings.length + 1 })}
            >
              <Repeat className="h-3 w-3" aria-hidden />
              {series.label}
            </span>
          ) : null}
          {inMotion ? <span className="text-label text-stevens-ink-3">{t('row.inProgress')}</span> : null}
          {task.metadata?.origin === 'auto' ? (
            <span
              className="text-label text-stevens-indigo"
              title={t('row.suggestedByTitle', { companion: companionName })}
            >
              {t('row.suggestedBy', { companion: companionName })}
            </span>
          ) : null}
        </div>

        <p className="flex flex-wrap items-center gap-x-1.5 text-caption text-stevens-ink-3">
          {facts.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-x-1.5 min-w-0">
              {i > 0 ? <span aria-hidden className="text-border">·</span> : null}
              {f}
            </span>
          ))}
        </p>

      </div>

      {/* #2420 follow-up: RowActions' touch hit area reaches 10px past each
          button, so on a coarse pointer the cluster spaces the series toggle
          12px away. Desktop is unchanged. */}
      <div className="flex shrink-0 items-center gap-1 [@media(pointer:coarse)]:gap-3">
        {series && series.siblings.length > 0 ? (
          <button
            type="button"
            onClick={() => setSeriesExpanded((v) => !v)}
            aria-expanded={seriesExpanded}
            // The 44 px touch box starts at the row's own top edge: -mt-2
            // cancels exactly the row's 8 px top padding (py-2), and -mb-3
            // keeps the margin box at 24 px, the cluster's height, so the row
            // keeps its height. The old -my-3 centred the box on the cluster
            // and reached ~2-4 px into the row above, taking its taps.
            className={cn(TEXT_HIT, '[@media(pointer:coarse)]:-mt-2 [@media(pointer:coarse)]:-mb-3 gap-1 text-label text-stevens-ink-3 hover:text-foreground')}
          >
            <ChevronDown className={cn('h-3 w-3 transition-transform', seriesExpanded && 'rotate-180')} aria-hidden />
            {seriesExpanded ? t('row.hideDates') : t('row.more', { count: series.siblings.length })}
          </button>
        ) : null}
        {!isCompleted ? (
          <RowActions
            editLabel={t('row.editTask')}
            deleteLabel={t('row.cancelTask')}
            onEdit={canEdit ? () => onEdit?.(task) : undefined}
            onDelete={onCancel ? () => onCancel(task._id) : undefined}
          />
        ) : null}
      </div>

      {/* #2499: the expanded dates sit OUTSIDE the content column, on a row
          of their own (`basis-full` in the wrapping row), so they span the
          full row width instead of the `flex-1` column squeezed beside the
          shrink-0 actions cluster — at 390 px each date now fits one line. */}
      {series && seriesExpanded && series.siblings.length > 0 ? (
        <ul className="mt-2 basis-full space-y-1 border-l border-border/60 pl-3">
          {series.siblings.map((sib) => {
            const sibDone = sib.status === 'completed';
            const sibGone = sib.status === 'cancelled';
            return (
              // #2420: on touch each date row is as tall as its 44 px buttons,
              // so a row's buttons never reach into its neighbour's.
              <li key={sib._id} className="flex items-center justify-between gap-2 text-caption [@media(pointer:coarse)]:min-h-11">
                <span className={cn(sibDone || sibGone ? 'text-stevens-ink-3 line-through' : 'text-foreground')}>
                  {occurrenceText(sib, t as unknown as LooseT) ?? labelFor(WHEN_LABEL_KEY, 'undated', t)}
                </span>
                {!sibDone && !sibGone ? (
                  <span className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => onComplete?.(sib._id)}
                      className={cn(TEXT_HIT, 'text-label text-stevens-ink-3 hover:text-foreground')}
                    >
                      {t('row.done')}
                    </button>
                    <button
                      type="button"
                      onClick={() => onCancel?.(sib._id)}
                      className={cn(TEXT_HIT, 'text-label text-stevens-ink-3 hover:text-status-danger-fg')}
                    >
                      {t('common:actions.cancel')}
                    </button>
                  </span>
                ) : sibDone ? (
                  <span className="text-label text-status-ok-fg">{t('row.doneLower')}</span>
                ) : null}
              </li>
            );
          })}
          {isOwner && series.onCancelAll ? (
            <li className="pt-1">
              <button
                type="button"
                onClick={series.onCancelAll}
                className={cn(TEXT_HIT, 'text-label text-stevens-ink-3 hover:text-status-danger-fg')}
              >
                {t('row.cancelSeries')}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
      {/* #2420: the taller touch rows and the text actions' widths depart
          from D-2420 A ("visuals unchanged") and await Victor's ruling. */}
      {series && seriesExpanded && series.siblings.length > 0 ? (
        <div className="mt-1 basis-full">
          <PendingDecision id="touch-target-series-rows" side="bottom" />
        </div>
      ) : null}
    </li>
  );
}
